/* eslint-disable no-unused-vars */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    FormControlLabel,
    Checkbox,
    Grid,
    Link,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axiosInstance from '../../helper/AxiosInstance';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginTop: theme.spacing(8),
    padding: theme.spacing(4),
    display: 'flex',
    borderRadius: '8px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        padding: theme.spacing(3),
        marginTop: theme.spacing(4),
    },
}));

const Login = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [credentials, setCredentials] = useState({
        email: '',
        password: ''
    });
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [showPassword, setShowPassword] = useState(false);

    // toggle function
    const handleTogglePassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await axiosInstance.get('/scoring/login', {
                params: {
                    userEmail: credentials.email,
                    userPassword: credentials.password
                }
            });

            // If backend explicitly sends failure
            if (response.data?.status === false) {
                throw new Error(response.data.message || 'Login failed');
            }

            // Validate API response
            if (!response.data || !response.data.body || !response.data.body.userRole) {
                throw new Error('Invalid response structure from server');
            }

            const { userRole, userTrack = 1, userName, userEmail } = response.data.body;

            // Normalize and validate role
            const normalizedRole = userRole.toLowerCase();
            const validRoles = ['admin', 'superadmin', 'j1', 'j2', 'j3'];
            if (!validRoles.includes(normalizedRole)) {
                throw new Error(`Invalid user role: ${userRole}`);
            }

            // Prepare user data
            const userData = {
                email: userEmail,
                userName: userName || 'User',
                userRole: normalizedRole,
                userTrack: parseInt(userTrack) || 1
            };

            sessionStorage.setItem('userData', JSON.stringify(userData));

            // Navigate based on role
            switch (normalizedRole) {
                case 'admin':
                    navigate('/admin/dashboard');
                    break;
                case 'superadmin':
                    navigate('/super-admin/dashboard');
                    break;
                default: // judge roles
                    navigate(`/judge/track/${userTrack}/dashboard`);
            }

        } catch (err) {
            console.error('Login error:', err);
            setError(
                err.response?.data?.message ||   // backend error message
                err.response?.data?.statusMessage ||
                err.message ||
                'Login failed. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="lg" sx={{ py: 4 }}>
            <StyledPaper elevation={3}>
                {/* Left Side - Welcome Content */}
                <Box sx={{
                    flex: 1,
                    padding: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                    [theme.breakpoints.down('md')]: {
                        borderRight: 'none',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        paddingBottom: theme.spacing(4),
                        marginBottom: theme.spacing(4),
                    },
                }}>
                    <Typography variant="h4" component="h1" sx={{
                        fontWeight: 'bold',
                        color: 'primary.main',
                        mb: 1
                    }}>
                        WELCOME TO
                    </Typography>
                    <Typography variant="h4" component="h2" sx={{
                        fontWeight: 'bold',
                        color: 'primary.main',
                        mb: 3
                    }}>
                        HEXAGON DESIGN & ENGINEERING
                    </Typography>
                    <Typography variant="h5" component="h3" sx={{
                        fontWeight: 'medium',
                        mb: 3
                    }}>
                        User Conference India 2025
                    </Typography>
                    <Typography variant="subtitle1" component="p" sx={{
                        fontStyle: 'italic',
                        mb: 2
                    }}>
                        Simulating Reality, Delivering Certainty
                    </Typography>
                </Box>

                {/* Right Side - Login Form */}
                <Box sx={{
                    flex: 1,
                    padding: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    [theme.breakpoints.down('md')]: {
                        padding: theme.spacing(2),
                    },
                }}>
                    <Typography variant="h5" component="h4" sx={{
                        fontWeight: 'bold',
                        mb: 3,
                        textAlign: 'center'
                    }}>
                        Login
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            width: '100%',
                            '& .MuiTextField-root': { mb: 2 }
                        }}
                    >
                        <TextField
                            required
                            fullWidth
                            label="Email"
                            autoComplete="email"
                            value={credentials.email}
                            onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.875rem',
                                }
                            }}
                        />
                        <TextField
                            required
                            fullWidth
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            value={credentials.password}
                            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.875rem',
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleTogglePassword}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {/* <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            color="primary"
                                            size="small"
                                        />
                                    }
                                    label="Remember me"
                                    sx={{
                                        '& .MuiTypography-root': {
                                            fontSize: '0.875rem',
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <Link href="#" variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    Forgot password?
                                </Link>
                            </Grid>
                        </Grid> */}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{
                                mt: 1,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                textTransform: 'none',
                                borderRadius: '4px'
                            }}
                        >
                            {loading ? 'Logging in...' : 'LOGIN'}
                        </Button>
                    </Box>
                </Box>
            </StyledPaper>
        </Container>
    );
};

export default Login;